import React from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  Platform,
  StyleProp,
  TextStyle,
  Dimensions,
} from "react-native";
import StyleSheet from "react-native-media-query";
import commonStyle from "../../assets/styles/common-style";
import { theme } from "../../theme/theme";
import { isTablet } from "react-native-device-info";

// Define an enum for card types
export enum CardType {
  Location = "location",
  Email = "email",
  Phone = "phone",
  Fax = "fax",
  Mail = "mail",
}

interface ContactCardProps {
  type: CardType;
  icon: any;
  title: string;
  content: string;
  onPress?: () => void;
  isFaded?: boolean;
  page?: string;
  responsive?: boolean;
  key?:number;
}

const { ids, styles } = StyleSheet.create({
  iconContainer: {
    width: "10%",
    alignItems: "center",
    justifyContent: "center",
    height: 50,
  },
  normalCard: {
    marginBottom: 16,
  },
  responsiveCard: {
    width: 370,
    "@media (max-width: 450px)": {
      width: Dimensions.get("screen").width * 0.9,
    },
  },
  contactAgentCard: {
    minHeight: 90,
    height: "auto",
    borderRadius: 13,
    backgroundColor: theme.colors.white + 20,

    borderStyle: "solid",
    borderWidth: 2,
    borderColor: theme.colors.white,
    padding: 10,
    paddingHorizontal: 16,
    overflow: "hidden",
  },
  contactAgentCardFaded: {
    minHeight: 90,
    height: "auto",
    borderRadius: 13,
    padding: 10,
    paddingHorizontal: 16,
    overflow: "hidden",
    backgroundColor: theme.colors.white + 20,
  },
  marginLeft: {
    marginLeft: 12,
    paddingLeft: 5,
  },
  title: {
    color: theme.colors.white,
    fontFamily: theme.fontFamily.robotoBold,
    ...Platform.select({
      web: { fontWeight: theme.fontweight.bold as any },
    }),
    fontSize: theme.fontsize.cs,
  },
  content: {
    color: theme.colors.white,
    marginTop: 6,
    fontSize: theme.fontsize.cs,
    fontFamily: theme.fontFamily.robotoRegular,
    maxWidth: "100%",
    ...Platform.select({
      web: {
        "@media (min-width: 200px)": {
          width: 200,
        },
        "@media (min-width: 450px)": {
          width: 400,
        },
      },
    }),
  },
  contentWithAddress: {
    color: theme.colors.white,
    marginTop: 6,
    fontSize: theme.fontsize.cs,
    minWidth: "75%",
    fontFamily: theme.fontFamily.robotoRegular,
    maxWidth: isTablet() ? "90%" : "95%",
    ...Platform.select({
      web: {
        paddingRight: "5%",
        "@media (min-width: 200px)": {
          width: 250,
        },
        "@media (min-width: 450px)": {
          width: 200,
        },
      },
    }),
  },
  contentWithEmail: {
    color: theme.colors.white,
    marginTop: 6,
    fontSize: theme.fontsize.cs,
    fontFamily: theme.fontFamily.robotoRegular,
    maxWidth: "90%",
    "@media (min-width: 200px)": {
      minWidth: "70%",
      maxWidth: isTablet()?"90%":"100%",
    },
    "@media (min-width: 1200px)": {
      minWidth: "70%",
      maxWidth: "100%",
    },
    paddingRight: isTablet() ? "5%" : "12%",
    ...Platform.select({
      web: {
        fontWeight: "400",
        paddingRight: "0%",
        "@media (min-width: 200px)": {
          width: 250,
        },
        "@media (min-width: 450px)": {
          width: 400,
        },
      },
    }),
  },
});

const iconStyles = {
  location: {
    icon: { width: 34, height: 55 },
    fadedIcon: { width: 58, height: 98 },
  },
  email: {
    icon: { width: 49, height: 70, marginTop: -15 },
    fadedIcon: { width: 98, height: 121 },
  },
  phone: {
    icon: { width: 49, height: 73.7 },
    fadedIcon: { width: 76, height: 116 },
  },
  fax: {
    icon: { width: 48.7, height: 42.8 },
    fadedIcon: { width: 98, height: 83 },
  },
  mail: {
    icon: { width: 38, height: 35.9, marginLeft: 8 },
    fadedIcon: { width: 93, height: 65 },
  },
};

const ContactCard = ({
  type,
  icon,
  title,
  content,
  onPress,
  isFaded = false,
  page,
  responsive,
  key
}: ContactCardProps) => {
  const CardComponent = onPress ? TouchableOpacity : View;

  const getContentStyle = (): StyleProp<TextStyle> => {
    if (content?.includes("@")) {
      return styles.contentWithEmail;
    }
    if (content?.includes(",")) {
      return styles.contentWithAddress;
    }
    return styles.content;
  };

  return (
    <CardComponent
      onPress={onPress}
      testID={`${page}-page-contact-card-${type}`}
      style={[responsive ? styles.responsiveCard:styles.normalCard]}
      dataSet={{ media: ids.responsiveCard }}
      key={key}
    >
      <View
        style={[
          isFaded ? styles.contactAgentCardFaded : styles.contactAgentCard,
          commonStyle.styles.rowFlex,
          commonStyle.styles.inMiddle,
        ]}
        dataSet={{ media: ids.contactAgentCard }}
      >
        <View
          style={styles.iconContainer}
          testID={`${page}-page-contact-card-icon-container-${type}`}
        >
          <Image
            resizeMode="contain"
            style={iconStyles[type].icon}
            source={icon}
          />
        </View>
        <View style={[styles.marginLeft]} dataSet={{ media: ids.marginLeft }}>
          <Text
            style={styles.title}
            dataSet={{ media: ids.title }}
            testID={`${page}-page-contact-card-key-${type}`}
          >
            {title}
          </Text>
          <Text
            style={[getContentStyle()]}
            dataSet={{
              media: content?.includes("@")
                ? ids.contentWithEmail
                : ids.content,
            }}
            testID={`${page}-page-contact-card-value-${type}`}
          >
            {content}
          </Text>
        </View>
        <Image
          style={[
            iconStyles[type].fadedIcon,
            {
              position: "absolute",
              right: 0,
              transform: [{ rotate: "-15deg" }],
              opacity: 0.1,
            },
          ]}
          source={icon}
          testID={`${page}-page-contact-card-bg-icon-${type}`}
        />
      </View>
    </CardComponent>
  );
};

export default ContactCard;
